import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Lowercase + strip accents so "chateau angelus" matches "Château Angélus".
 * @param {string} str
 * @returns {string}
 */
function normalizeForProducerSearch(str) {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Locale-aware producer page pathname (matches Liquid routes.root_url + pages/producer/).
 * @param {string} handle
 * @returns {string}
 */
function producerPageUrl(handle) {
  const raw =
    typeof Theme !== 'undefined' && Theme.routes && Theme.routes.root_url != null
      ? String(Theme.routes.root_url)
      : '/';
  const base = raw.replace(/\/+$/, '');
  const tail = `/pages/producer/${String(handle).toLowerCase()}`;
  return base === '' ? tail : `${base}${tail}`;
}

/**
 * @param {Record<string, unknown>} node
 * @returns {string}
 */
function producerNodeDisplayName(node) {
  if (!node || typeof node !== 'object') return '';
  const o = /** @type {{ title?: { value?: string }; name?: { value?: string }; producer_title?: { value?: string }; handle?: string }} */ (
    node
  );
  return (
    (o.producer_title?.value && String(o.producer_title.value)) ||
    (o.title?.value && String(o.title.value)) ||
    (o.name?.value && String(o.name.value)) ||
    (o.handle && String(o.handle)) ||
    ''
  );
}

const STOREFRONT_QUERY = `
  query ProducerCatalogSearch($type: String!, $cursor: String) {
    metaobjects(type: $type, first: 250, after: $cursor) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        handle
        title: field(key: "title") { value }
        name: field(key: "name") { value }
        producer_title: field(key: "producer_title") { value }
      }
    }
  }
`;

/**
 * A custom element that allows users to search/filter producer metaobjects.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resetButton - The reset button element.
 * @extends {Component<Refs>}
 */
class ProducerSearchInputComponent extends Component {
  requiredRefs = ['searchInput', 'resetButton'];

  #controller = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #remoteSearchTimer = null;

  /** @type {AbortController | null} */
  #remoteFetchAbort = null;

  /** @type {number} */
  #remoteGeneration = 0;

  /**
   * Get the section ID
   * @returns {string} The section ID
   */
  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  connectedCallback() {
    super.connectedCallback();

    const initialValue = this.refs.searchInput.value.trim();
    if (initialValue.length > 0) {
      this.#showResetButton();
      this.#filterProducers(initialValue);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#remoteSearchTimer) clearTimeout(this.#remoteSearchTimer);
    this.#remoteFetchAbort?.abort();
  }

  /**
   * Handles search input with debouncing
   */
  handleSearchInput = debounce(() => {
    this.#performSearch();
  }, 300);

  /**
   * Handles keyboard events
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.refs.searchInput.value === '') {
        this.refs.searchInput.blur();
      } else {
        this.handleClearClick();
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.#performSearch();
    }
  };

  /**
   * Handles clear button click
   */
  handleClearClick = () => {
    this.refs.searchInput.value = '';
    this.refs.searchInput.focus();
    this.#hideResetButton();
    this.#performSearch();
  };

  /**
   * Performs the search by filtering producers client-side
   */
  #performSearch() {
    const searchTerm = this.refs.searchInput.value.trim();

    this.#updateURL(searchTerm);

    if (searchTerm.length > 0) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }

    this.#filterProducers(searchTerm);
  }

  #getProducerSearchConfig() {
    if (typeof window === 'undefined') return null;
    return /** @type {Window & { __producerSearchConfig?: Record<string, string> }} */ (window).__producerSearchConfig ?? null;
  }

  #removeRemoteSearchUI() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;
    section.querySelectorAll('.producer-search-remote').forEach((el) => el.remove());
  }

  /**
   * @param {HTMLElement} section
   */
  #showRemoteSearching(section) {
    this.#removeRemoteSearchUI();
    const grid = section.querySelector('.producer-cards-grid');
    if (!grid) return;
    const el = document.createElement('div');
    el.className = 'producer-search-remote producer-search-remote--loading';
    el.setAttribute('role', 'status');
    el.style.cssText = 'grid-column: 1 / -1; padding: var(--padding-md); text-align: center;';
    el.textContent = 'Searching catalog…';
    grid.insertBefore(el, grid.firstChild);
  }

  /**
   * @param {HTMLElement} section
   * @param {string} displayName
   * @param {string} handle
   */
  #showRemoteMatch(section, displayName, handle) {
    this.#removeRemoteSearchUI();
    const grid = section.querySelector('.producer-cards-grid');
    if (!grid) return;
    const path = producerPageUrl(handle);
    const wrap = document.createElement('div');
    wrap.className = 'producer-search-remote producer-search-remote--match';
    wrap.style.cssText = 'grid-column: 1 / -1; padding: var(--padding-md); text-align: center;';
    const lead = document.createElement('p');
    lead.style.margin = '0 0 var(--margin-sm)';
    lead.textContent = 'Not on this page — match in catalog:';
    const line = document.createElement('p');
    line.style.margin = '0';
    const a = document.createElement('a');
    a.href = path;
    a.style.textDecoration = 'underline';
    a.textContent = displayName || handle;
    line.appendChild(a);
    wrap.appendChild(lead);
    wrap.appendChild(line);
    grid.insertBefore(wrap, grid.firstChild);
  }

  /**
   * @param {string} searchTerm
   * @param {string} normalizedTerm
   * @param {number} generation
   */
  async #runRemoteProducerSearch(searchTerm, normalizedTerm, generation) {
    const cfg = this.#getProducerSearchConfig();
    const token = cfg?.storefrontAccessToken ? String(cfg.storefrontAccessToken) : '';
    const shopDomain = cfg?.shopDomain ? String(cfg.shopDomain) : '';
    const type = cfg?.metaobjectType ? String(cfg.metaobjectType) : 'producer';
    const apiVersion = cfg?.apiVersion ? String(cfg.apiVersion) : '2024-01';

    if (!token || !shopDomain || normalizedTerm.length < 2) {
      this.#updateEmptyState(searchTerm, 0, { skipRemote: true });
      return;
    }

    this.#remoteFetchAbort?.abort();
    this.#remoteFetchAbort = new AbortController();

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (section) this.#showRemoteSearching(section);

    const endpoint = `https://${shopDomain}/api/${apiVersion}/graphql.json`;
    let cursor = null;
    let hasNext = true;
    let pages = 0;
    const maxPages = 30;

    try {
      while (hasNext && pages < maxPages) {
        if (generation !== this.#remoteGeneration) return;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query: STOREFRONT_QUERY,
            variables: { type, cursor },
          }),
          signal: this.#remoteFetchAbort.signal,
        });

        const json = await res.json();
        if (json.errors?.length) {
          console.warn('[producer-search] Storefront:', json.errors);
          break;
        }

        const meta = json.data?.metaobjects;
        const nodes = meta?.nodes || [];
        for (const node of nodes) {
          const name = normalizeForProducerSearch(producerNodeDisplayName(node));
          const handle = node?.handle ? String(node.handle) : '';
          if (name.includes(normalizedTerm) && handle) {
            if (generation !== this.#remoteGeneration) return;
            this.#removeRemoteSearchUI();
            if (section) this.#showRemoteMatch(section, producerNodeDisplayName(node) || handle, handle);
            this.#updateEmptyState(searchTerm, 0, { hideEmptyCopy: true });
            return;
          }
        }

        hasNext = meta?.pageInfo?.hasNextPage === true;
        cursor = meta?.pageInfo?.endCursor || null;
        pages += 1;
        if (!hasNext || !cursor) break;
      }

      if (generation !== this.#remoteGeneration) return;
      this.#removeRemoteSearchUI();
      this.#updateEmptyState(searchTerm, 0, { skipRemote: true });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.warn('[producer-search] Catalog search failed', e);
      if (generation !== this.#remoteGeneration) return;
      this.#removeRemoteSearchUI();
      this.#updateEmptyState(searchTerm, 0, { skipRemote: true });
    }
  }

  /**
   * @param {string} searchTerm
   * @param {string} normalizedTerm
   * @param {number} visibleCount
   */
  #queueRemoteProducerSearch(searchTerm, normalizedTerm, visibleCount) {
    if (this.#remoteSearchTimer) clearTimeout(this.#remoteSearchTimer);
    this.#remoteFetchAbort?.abort();

    const cfg = this.#getProducerSearchConfig();
    if (!cfg?.storefrontAccessToken || normalizedTerm.length < 2) {
      this.#updateEmptyState(searchTerm, visibleCount, { skipRemote: true });
      return;
    }

    this.#remoteGeneration += 1;
    const gen = this.#remoteGeneration;

    const sectionEarly = document.getElementById(`shopify-section-${this.sectionId}`);
    if (sectionEarly) this.#showRemoteSearching(sectionEarly);

    this.#remoteSearchTimer = setTimeout(() => {
      this.#remoteSearchTimer = null;
      void this.#runRemoteProducerSearch(searchTerm, normalizedTerm, gen);
    }, 450);
  }

  /**
   * Filters producers by hiding/showing them based on search term
   * @param {string} searchTerm - Raw search text (accents normalized for matching)
   */
  #filterProducers(searchTerm) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    this.#remoteFetchAbort?.abort();
    if (this.#remoteSearchTimer) {
      clearTimeout(this.#remoteSearchTimer);
      this.#remoteSearchTimer = null;
    }
    this.#remoteGeneration += 1;
    this.#removeRemoteSearchUI();

    const producerCards = section.querySelectorAll('.producer-card[data-producer-name]');
    let visibleCount = 0;
    const normalizedTerm = normalizeForProducerSearch(searchTerm);

    producerCards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      if (!normalizedTerm) {
        card.style.display = '';
        visibleCount++;
        return;
      }

      const name = normalizeForProducerSearch(card.dataset.producerName || '');
      const location = normalizeForProducerSearch(card.dataset.producerLocation || '');

      const matches = name.includes(normalizedTerm) || location.includes(normalizedTerm);

      if (matches) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    if (normalizedTerm && visibleCount === 0) {
      this.#queueRemoteProducerSearch(searchTerm, normalizedTerm, visibleCount);
    } else {
      this.#updateEmptyState(searchTerm, visibleCount, { skipRemote: true });
    }
  }

  /**
   * Updates the URL with the search parameter
   * @param {string} searchTerm - The search term
   */
  #updateURL(searchTerm) {
    const url = new URL(window.location.href);

    if (searchTerm) {
      url.searchParams.set('q', searchTerm);
    } else {
      url.searchParams.delete('q');
    }

    history.pushState({}, '', url.toString());
  }

  /**
   * @param {string} searchTerm
   * @param {number} visibleCount
   * @param {{ skipRemote?: boolean; hideEmptyCopy?: boolean }} [opts]
   */
  #updateEmptyState(searchTerm, visibleCount, opts = {}) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const existingEmptyState = section.querySelector('.producer-search-empty-state');
    if (existingEmptyState) {
      existingEmptyState.remove();
    }

    if (opts.hideEmptyCopy) return;

    if (searchTerm && visibleCount === 0 && opts.skipRemote) {
      const producerGrid = section.querySelector('.producer-cards-grid');
      if (producerGrid?.parentElement) {
        const emptyState = document.createElement('div');
        emptyState.className = 'producer-search-empty-state';
        emptyState.style.cssText = 'grid-column: 1 / -1; padding: var(--padding-xl); text-align: center;';
        const inputId = this.refs.searchInput.id || 'producer-search-q';
        emptyState.innerHTML = `
          <h2 class="h2" style="margin-bottom: var(--margin-md);">No producers found</h2>
          <p style="margin-bottom: var(--margin-md);">No producers match your search for "${searchTerm.replace(/"/g, '&quot;')}".</p>
          <button type="button" class="button button--secondary producer-search-empty-clear" data-producer-search-clear>
            Clear search
          </button>
        `;
        const btn = emptyState.querySelector('[data-producer-search-clear]');
        btn?.addEventListener('click', () => this.handleClearClick());
        producerGrid.parentElement.insertBefore(emptyState, producerGrid);
      }
    }
  }

  /**
   * Shows the reset button
   */
  #showResetButton() {
    this.refs.resetButton.style.opacity = '1';
    this.refs.resetButton.style.visibility = 'visible';
    this.refs.resetButton.style.pointerEvents = 'auto';
  }

  /**
   * Hides the reset button
   */
  #hideResetButton() {
    this.refs.resetButton.style.opacity = '0';
    this.refs.resetButton.style.visibility = 'hidden';
    this.refs.resetButton.style.pointerEvents = 'none';
  }
}

if (!customElements.get('producer-search-input')) {
  customElements.define('producer-search-input', ProducerSearchInputComponent);
}
